import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Settings2, Plus, Trash2, Play, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Tag, Building2, Zap
} from 'lucide-react';
import { apiService, DepartmentRule, Department } from '../services/api';
import toast from 'react-hot-toast';

const MATCH_FIELD_LABELS: Record<string, string> = {
  description: 'Description',
  platform_account_id: 'Platform Account',
  'metadata.sku': 'Line Item SKU',
  amount: 'Amount',
  customer_email: 'Customer Email',
};

const MATCH_OPERATOR_LABELS: Record<string, string> = {
  contains: 'Contains',
  equals: 'Equals',
  starts_with: 'Starts With',
  ends_with: 'Ends With',
  regex: 'Regex',
};

const RULE_TYPE_STYLES: Record<string, { label: string; cls: string }> = {
  dedicated_account: { label: 'Dedicated Account', cls: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  keyword_match: { label: 'Keyword Match', cls: 'text-purple-700 bg-purple-50 ring-purple-600/20' },
  metadata_match: { label: 'Metadata Match', cls: 'text-teal-700 bg-teal-50 ring-teal-600/20' },
  amount_match: { label: 'Amount Match', cls: 'text-pink-700 bg-pink-50 ring-pink-600/20' },
  default: { label: 'Default/Fallback', cls: 'text-gray-700 bg-gray-50 ring-gray-600/20' },
};

const CategorizationRules: React.FC = () => {
  const [rules, setRules] = useState<DepartmentRule[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // New rule form state
  const [newRule, setNewRule] = useState({
    department_id: '',
    priority: 50,
    rule_type: 'keyword_match',
    match_field: 'description',
    match_operator: 'contains',
    match_value: '',
    description: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      await apiService.loadDepartments();
      const [rulesData, statsData] = await Promise.all([
        apiService.getDepartmentRules(),
        apiService.getDepartmentStats(),
      ]);
      setRules(rulesData);
      setDepartments(apiService.getDepartments());
      setStats(statsData);
    } catch (error) {
      console.error('Error loading categorization data:', error);
      toast.error('Failed to load categorization data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleRunCategorization = async () => {
    setRunning(true);
    try {
      const result = await apiService.runCategorization();
      if (result) {
        toast.success(`Categorization complete: ${result.categorized || 0} transactions updated`);
        await loadData();
      } else {
        toast.error('Categorization failed');
      }
    } catch (error) {
      toast.error('Categorization failed');
    } finally {
      setRunning(false);
    }
  };

  const handleAddRule = async () => {
    if (!newRule.department_id || !newRule.match_value) {
      toast.error('Department and match value are required');
      return;
    }
    const created = await apiService.createDepartmentRule(newRule);
    if (created) {
      toast.success('Rule created');
      setShowAddForm(false);
      setNewRule({ department_id: '', priority: 50, rule_type: 'keyword_match', match_field: 'description', match_operator: 'contains', match_value: '', description: '' });
      await loadData();
    } else {
      toast.error('Failed to create rule');
    }
  };

  const handleDeleteRule = async (id: string) => {
    if (!confirm('Deactivate this rule?')) return;
    const ok = await apiService.deleteDepartmentRule(id);
    if (ok) {
      toast.success('Rule deactivated');
      await loadData();
    } else {
      toast.error('Failed to deactivate rule');
    }
  };

  const handleUpdatePriority = async (id: string, newPriority: number) => {
    const updated = await apiService.updateDepartmentRule(id, { priority: newPriority });
    if (updated) {
      setRules(prev => prev.map(r => r.id === id ? { ...r, priority: newPriority } : r).sort((a, b) => a.priority - b.priority));
    }
  };

  const getDeptName = (deptId: string) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading categorization rules...</span>
      </div>
    );
  }

  const groupedRules = rules.reduce<Record<number, DepartmentRule[]>>((acc, rule) => {
    if (!acc[rule.priority]) acc[rule.priority] = [];
    acc[rule.priority].push(rule);
    return acc;
  }, {});
  const sortedPriorities = Object.keys(groupedRules).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorization Rules</h1>
          <p className="text-sm text-gray-500">Manage how transactions are assigned to departments</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="!bg-white !text-gray-700 border border-gray-300 hover:!bg-gray-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>
          <Button
            onClick={handleRunCategorization}
            disabled={running}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Play className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running...' : 'Run Categorization'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center space-x-2">
                <Tag className="h-4 w-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Rules</p>
                  <p className="text-xl font-bold text-gray-900">{rules.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase">Categorized</p>
                  <p className="text-xl font-bold text-gray-900">{(stats.categorized || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase">Uncategorized</p>
                  <p className="text-xl font-bold text-gray-900">{(stats.uncategorized || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase">Auto Rate</p>
                  <p className="text-xl font-bold text-gray-900">
                    {stats.categorized && stats.total
                      ? `${((stats.categorized / stats.total) * 100).toFixed(1)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-teal-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase">Departments</p>
                  <p className="text-xl font-bold text-gray-900">{departments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Categorization Method Breakdown */}
      {stats?.byMethod && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Categorization Methods</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.byMethod as Record<string, number>)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([method, count]) => {
                  const total = stats.total || 1;
                  const pct = ((count as number) / total) * 100;
                  const style = RULE_TYPE_STYLES[method] || { label: method.replace(/_/g, ' '), cls: 'text-gray-700 bg-gray-50 ring-gray-600/20' };
                  return (
                    <div key={method} className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ring-1 ring-inset w-36 justify-center ${style.cls}`}>
                        {style.label}
                      </span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm text-gray-600 w-20 text-right">{(count as number).toLocaleString()}</span>
                      <span className="text-xs text-gray-400 w-12 text-right">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Rule Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">New Categorization Rule</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.department_id}
                  onChange={(e) => setNewRule(prev => ({ ...prev, department_id: e.target.value }))}
                >
                  <option value="">Select department...</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Priority (lower = runs first)</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.priority}
                  onChange={(e) => setNewRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 50 }))}
                  min={1}
                  max={100}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rule Type</label>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.rule_type}
                  onChange={(e) => setNewRule(prev => ({ ...prev, rule_type: e.target.value }))}
                >
                  {Object.entries(RULE_TYPE_STYLES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Match Field</label>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.match_field}
                  onChange={(e) => setNewRule(prev => ({ ...prev, match_field: e.target.value }))}
                >
                  {Object.entries(MATCH_FIELD_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Operator</label>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.match_operator}
                  onChange={(e) => setNewRule(prev => ({ ...prev, match_operator: e.target.value }))}
                >
                  {Object.entries(MATCH_OPERATOR_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Match Value</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.match_value}
                  onChange={(e) => setNewRule(prev => ({ ...prev, match_value: e.target.value }))}
                  placeholder="e.g., 3 day school, stripe_1"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newRule.description}
                  onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Human-readable explanation of this rule"
                />
              </div>
              <div className="flex items-end space-x-2">
                <Button onClick={handleAddRule} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-1" /> Create Rule
                </Button>
                <Button onClick={() => setShowAddForm(false)} className="!bg-white !text-gray-700 border border-gray-300 hover:!bg-gray-50">Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules by Priority */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center space-x-2">
            <Settings2 className="h-5 w-5 text-gray-500" />
            <CardTitle>Active Rules</CardTitle>
          </div>
          <p className="text-xs text-gray-400">Rules run in priority order (lowest number first)</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase w-20">Priority</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Department</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Match</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedPriorities.map(priority => (
                  <React.Fragment key={priority}>
                    {/* Priority group header */}
                    <tr className="bg-gray-50/50">
                      <td colSpan={6} className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                        Priority {priority}
                      </td>
                    </tr>
                    {groupedRules[priority].map(rule => {
                      const typeStyle = RULE_TYPE_STYLES[rule.rule_type] || { label: rule.rule_type, cls: 'text-gray-700 bg-gray-50 ring-gray-600/20' };
                      return (
                        <tr key={rule.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 px-3 text-center">
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleUpdatePriority(rule.id, Math.max(1, rule.priority - 1))}
                                className="p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                                title="Increase priority (lower number)"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </button>
                              <span className="text-sm font-mono font-medium text-gray-700 w-6 text-center">{rule.priority}</span>
                              <button
                                onClick={() => handleUpdatePriority(rule.id, Math.min(100, rule.priority + 1))}
                                className="p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                                title="Decrease priority (higher number)"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ring-1 ring-inset ${typeStyle.cls}`}>
                              {typeStyle.label}
                            </span>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-sm font-medium text-gray-900">
                              {rule.department?.name || getDeptName(rule.department_id)}
                            </span>
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">{MATCH_FIELD_LABELS[rule.match_field] || rule.match_field}</span>
                              <span className="text-xs text-gray-400">{rule.match_operator}</span>
                              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-800 font-mono">
                                {rule.match_value}
                              </code>
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-sm text-gray-500 truncate max-w-[250px] block">
                              {rule.description || '—'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              onClick={() => handleDeleteRule(rule.id)}
                              className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                              title="Deactivate rule"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">How Categorization Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 space-y-2">
            <p>
              Transactions are categorized by running rules in <strong>priority order</strong> (lowest number first).
              The first matching rule wins and assigns the transaction to that department.
            </p>
            <p>
              For example, a rule at priority 10 (keyword match on description containing "3 day school")
              runs before a priority 50 rule (dedicated account fallback for stripe_1). This means specific
              keyword matches take precedence over broad account-level defaults.
            </p>
            <p className="text-gray-400 text-xs">
              Transactions that don't match any rule are placed in the "Uncategorized" department.
              Use "Run Categorization" to re-process uncategorized transactions after adding or changing rules.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CategorizationRules;
